import { DialogClose } from '@radix-ui/react-dialog';
import { Button } from '@studio/design-system/button';
import { Sel, STagNew } from '#~/stag/index.js';
import type { ReactElement } from 'react';

interface OutputFooterProps {
  isOutputting: boolean;
  isStarting: boolean;
  isStopping: boolean;
  hasChanges: boolean;
  onSave: () => void;
}

/**
 * Footer component for the output section that displays control buttons
 *
 * @param props - The component props.
 * @param props.isOutputting - Whether output is currently active.
 * @param props.isStarting - Whether output is in the process of starting.
 * @param props.isStopping - Whether output is in the process of stopping.
 * @param props.hasChanges - Whether there are unsaved changes.
 * @param props.onSave - Callback function to handle saving changes.
 * @returns A React element representing the output footer.
 */
export const OutputFooter = ({
  isOutputting,
  isStarting,
  isStopping,
  hasChanges,
  onSave
}: OutputFooterProps): ReactElement => {
  return (
    // STags, not accessible names: the primary button's label swaps between
    // Start Output / Save Changes / Starting. Per rule:
    // playwright-stag-selectors-only (ai.knowledge/platform). Cancel shares the
    // Recording footer's tag — only the mounted tab renders a visible copy.
    <div className='flex justify-between bg-base-100 px-40 py-24'>
      <DialogClose>
        <Button variant='secondary' {...STagNew(Sel.Production, 'cancel')}>
          Cancel
        </Button>
      </DialogClose>
      <Button
        variant={isOutputting ? 'primary' : hasChanges ? 'danger' : 'secondary'}
        disabled={!hasChanges || isStarting || isStopping}
        onClick={onSave}
        {...STagNew(Sel.ProductionOutput, 'save')}
      >
        {(() => {
          if (isOutputting && isStopping) return 'Stopping';
          if (isOutputting) return 'Save Changes';
          if (isStarting) return 'Starting';
          return 'Start Output';
        })()}
      </Button>
    </div>
  );
};
